import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../i18n';
import { SlideOver, SlideOverFooter } from '../../../components/Shared/SlideOver';
import { FormField } from '../../../components/Shared/FormField';
import {
  SnippetTextArea,
  type InvoiceSnippetContext,
} from '../../../components/Shared/SnippetTextArea';
import { ContactPersonFields } from '../../../components/Contacts/ContactPersonFields';
import { useObjectStore } from '../../../store/useObjectStore';
import { useTextSnippetStore } from '../../../store/useTextSnippetStore';
import type { FacilityFlowCoreModulesInvoicesDtosInvoiceDto } from '../../../api/generated/models';
import { toSalutation, type Salutation } from '../../../utils/salutation';
import { isStandingInvoice, toBillingInterval } from '../../../types/billing';

type InvoiceDto = FacilityFlowCoreModulesInvoicesDtosInvoiceDto;

/** Header fields that stay editable while the invoice is a draft (ADR-0018, Api repo). */
export interface EditInvoiceFormData {
  contactPersonName: string;
  contactSalutation: Salutation | null;
  serviceDateFrom: string;
  serviceDateTo: string;
  buyerReference: string;
  introductionText: string;
  closingText: string;
  paymentTerms: string;
}

interface EditInvoiceSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: InvoiceDto;
  saving: boolean;
  onSave: (form: EditInvoiceFormData) => void | Promise<void>;
}

const toDateOnly = (value?: string | null) => value?.split('T')[0] ?? '';

const toForm = (invoice: InvoiceDto): EditInvoiceFormData => ({
  contactPersonName: invoice.contactPersonName ?? '',
  contactSalutation: toSalutation(invoice.contactSalutation),
  serviceDateFrom: toDateOnly(invoice.serviceDateFrom),
  serviceDateTo: toDateOnly(invoice.serviceDateTo),
  buyerReference: invoice.buyerReference ?? '',
  introductionText: invoice.introductionText ?? '',
  closingText: invoice.closingText ?? '',
  paymentTerms: invoice.paymentTerms ?? '',
});

export function EditInvoiceSlideOver({
  isOpen, onClose, invoice, saving, onSave,
}: EditInvoiceSlideOverProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const [form, setForm] = useState<EditInvoiceFormData>(() => toForm(invoice));
  const fetchSnippets = useTextSnippetStore((s) => s.fetchSnippets);
  const [preferredContactId, setPreferredContactId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) setForm(toForm(invoice));
  }, [isOpen, invoice]);

  useEffect(() => {
    if (isOpen) fetchSnippets({ isActive: true });
  }, [isOpen, fetchSnippets]);

  // why: a stored contact name is never overwritten by the auto-pick (FACF-102), so the
  // object's responsible contact is only worth loading when the draft has no name yet.
  useEffect(() => {
    if (!isOpen || !invoice.objectId || (invoice.contactPersonName ?? '') !== '') {
      setPreferredContactId(null);
      return;
    }
    let cancelled = false;
    useObjectStore
      .getState()
      .getObjectById(invoice.objectId)
      .then((object) => {
        if (!cancelled) setPreferredContactId(object?.responsibleContactId ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, invoice.objectId, invoice.contactPersonName]);

  const update = <K extends keyof EditInvoiceFormData>(
    key: K, value: EditInvoiceFormData[K],
  ) => setForm((f) => ({ ...f, [key]: value }));

  // why: mirrors what UpdateInvoiceWorkflow renders on save (ADR-0020, Api repo): number,
  // invoice date and due date only exist once the invoice is issued — until then the
  // backend omits them and the tokens stay raw, exactly as the preview shows.
  // why: standing invoices pick from their own snippet categories (7/8) so the picker
  // offers the "monatliche Überweisung" texts instead of the monthly-invoice defaults.
  const standing = isStandingInvoice(invoice.billingInterval);
  const renderContext: InvoiceSnippetContext = useMemo(
    () => ({
      customerId: invoice.customerId ?? null,
      objectId: invoice.objectId ?? null,
      contactSalutation: form.contactSalutation,
      contactPersonName: form.contactPersonName || null,
      invoiceNumber: invoice.invoiceNumber || null,
      invoiceDate: toDateOnly(invoice.issueDate) || null,
      dueDate: toDateOnly(invoice.dueDate) || null,
      serviceDateFrom: form.serviceDateFrom || null,
      serviceDateTo: form.serviceDateTo || null,
      billingPeriodYear: invoice.billingPeriodYear ?? null,
      billingPeriodMonth: invoice.billingPeriodMonth ?? null,
      // why: {BillingPeriod} renders "Q3/2026" / "Jänner bis Dezember 2026" only when the
      // preview knows the interval; otherwise it would differ from the saved text (ADR-0020).
      interval: toBillingInterval(invoice.billingInterval),
      taxRate: invoice.taxRate ?? null,
    }),
    [
      invoice.customerId, invoice.objectId, invoice.invoiceNumber, invoice.issueDate,
      invoice.dueDate, invoice.billingPeriodYear, invoice.billingPeriodMonth,
      invoice.billingInterval, invoice.taxRate,
      form.contactSalutation, form.contactPersonName, form.serviceDateFrom, form.serviceDateTo,
    ],
  );

  // ISO dates (YYYY-MM-DD) compare correctly as strings.
  const datesMissing = !form.serviceDateFrom || !form.serviceDateTo;
  const dateRangeInvalid = !datesMissing && form.serviceDateTo < form.serviceDateFrom;
  // why: UpdateInvoiceRequest marks these [Required]; an empty value would either be
  // rejected with an unlocalised 400 or silently blank the text on the PDF.
  const contactMissing = form.contactPersonName.trim() === '';
  const introductionMissing = form.introductionText.trim() === '';
  const closingMissing = form.closingText.trim() === '';
  const confirmDisabled =
    datesMissing || dateRangeInvalid || contactMissing || introductionMissing || closingMissing;

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('invoices:actions.edit')}
      size="lg"
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={() => onSave(form)}
          confirmText={t('common:actions.save')}
          confirmDisabled={confirmDisabled}
          loading={saving}
        />
      }
    >
      <div className="space-y-4">
        <ContactPersonFields
          customerId={invoice.customerId}
          salutation={form.contactSalutation}
          name={form.contactPersonName}
          onChange={({ salutation, name }) =>
            setForm((f) => ({ ...f, contactSalutation: salutation, contactPersonName: name }))
          }
          preferredContactId={preferredContactId}
          nameLabel={t('invoices:fields.contactPerson')}
          nameRequired
          nameError={contactMissing ? t('invoices:validation.contactPersonRequired') : undefined}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label={t('invoices:fields.serviceDateFrom')}
            type="date"
            required
            disabled={standing}
            value={form.serviceDateFrom}
            onChange={(e) => update('serviceDateFrom', e.target.value)}
          />
          <FormField
            label={t('invoices:fields.serviceDateTo')}
            type="date"
            required
            disabled={standing}
            value={form.serviceDateTo}
            onChange={(e) => update('serviceDateTo', e.target.value)}
            error={dateRangeInvalid ? t('invoices:manualCreate.errors.dateRange') : undefined}
          />
        </div>
        {/* why: the service period of a standing draft is fixed by its payment plan
            (invoices.standingDraftLocked, FAFD-27). */}
        {standing && (
          <p className="-mt-2 text-[11px] text-slate-500">{t('invoices:hints.standingDraftLocked')}</p>
        )}
        <FormField
          label={t('invoices:fields.buyerReference')}
          value={form.buyerReference}
          onChange={(e) => update('buyerReference', e.target.value)}
          maxLength={100}
        />
        <SnippetTextArea
          label={t('invoices:fields.introductionText')}
          name="introductionText"
          required
          value={form.introductionText}
          onChange={(value) => update('introductionText', value)}
          category={standing ? 'StandingInvoiceIntroduction' : 'InvoiceIntroduction'}
          documentType="invoice"
          rows={3}
          maxLength={4000}
          error={introductionMissing ? t('invoices:validation.introductionRequired') : undefined}
          renderContext={renderContext}
        />
        <SnippetTextArea
          label={t('invoices:fields.closingText')}
          name="closingText"
          required
          value={form.closingText}
          onChange={(value) => update('closingText', value)}
          category={standing ? 'StandingInvoiceClosingText' : 'InvoiceClosingText'}
          documentType="invoice"
          rows={3}
          maxLength={4000}
          error={closingMissing ? t('invoices:validation.closingRequired') : undefined}
          renderContext={renderContext}
        />
        <SnippetTextArea
          label={t('invoices:fields.paymentTerms')}
          name="paymentTerms"
          value={form.paymentTerms}
          onChange={(value) => update('paymentTerms', value)}
          category="InvoicePaymentTerms"
          documentType="invoice"
          rows={2}
          maxLength={2000}
          renderContext={renderContext}
        />
      </div>
    </SlideOver>
  );
}

export default EditInvoiceSlideOver;
